import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { getGroupField } from '@databases/utils/database/view/view';
import React, { FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useScope } from '../../Scope';

const StyledSkeletonItem = styled(SkeletonItem)`
  &&& {
    height: 40px;
  }
`;

const StyledButton = styled(Button)`
  && {
    height: 40px;
    width: 40px;
  }

  svg {
    height: 16px;
    width: auto;
  }
`;

interface BoardLayoutAddOptionButtonProps {
  contentId: string;
  viewSettings: DatabaseViewSettings;
}

const BoardLayoutAddOptionButton: FunctionComponent<BoardLayoutAddOptionButtonProps> = ({
  contentId,
  viewSettings,
}) => {
  const scope = useScope();
  const { showPanel } = usePanel();
  const [groupFieldId] = defaultStoreUtils.useEqualSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    const groupField = getGroupField(databaseDraft, viewSettings.layout)!;
    return [groupField.id] as const;
  });

  const { formatMessage } = useIntl();

  const handleClick = useFlaggingCallback(async () => {
    showPanel('FieldSettingsPanel', { fieldId: groupFieldId, createSelectOption: true });
  }, [showPanel, groupFieldId]);

  return (
    <StyledSkeletonItem isButton>
      <StyledButton
        onClick={handleClick}
        iconBefore={
          <AddIcon label={formatMessage(messages['layout.board-add-option'])} />
        }
      />
    </StyledSkeletonItem>
  );
};

export default BoardLayoutAddOptionButton;
